import { FC, useEffect, useState } from 'react'
import { Product, ProductColor } from '@/network/graphql.g'
import { Box } from '@/components/atoms/Grid'
import Link from '@/components/atoms/link'
import { Routes } from '@/core/routes'
import useInView from 'react-cool-inview'
import { useMhub } from '@/providers/mhubProvider'
import { ID } from 'graphql-ws'
import styled, { css } from 'styled-components'
import { PictureWithPlaceholder } from '@/components/atoms/picture'
import { applyImagePolicy, ImageMode, ImageSizes } from '@/utils/imageExtension'

type ProductDetailColorProps = {
  color: ProductColor
  productBrand: string
  mainCategoryId: ID
  position: number
  setHoverColorVariant?: (hoverColorVariant: string) => void
  setHoverColorId?: (hoverColorId: string) => void
  currentImage?: boolean
  unavailableProduct?: Product
} & Rest

const ProductDetailColor: FC<ProductDetailColorProps> = ({
  color,
  productBrand,
  mainCategoryId,
  position,
  setHoverColorVariant,
  setHoverColorId,
  currentImage = false,
  unavailableProduct,
  ...rest
}) => {
  const { image, id } = color
  const mhubProductColor = {
    ...color,
    productBrand,
    mainCategoryId
  }
  const { pushImpression, getImpressionAttributeData, pushShowProductEvent } =
    useMhub()
  const list = `list.detail.color_variants.${color?.colorGroupId}.${id}-${color?.MHubProperties?.name}`

  const { observe } = useInView({
    unobserveOnEnter: true,
    threshold: [0.5],
    onEnter: () => {
      {
        mhubProductColor.MHubProperties &&
          pushImpression(mhubProductColor, position, list)
      }
    }
  })

  const [client, setClient] = useState<boolean>(false)
  useEffect(() => {
    setClient(true)
  }, [])

  const handleMouseEvent = (imageUrl: string, id: string) => {
    setHoverColorVariant?.(imageUrl)
    if (unavailableProduct) {
      setHoverColorId(id)
    }
  }

  return (
    <div ref={observe}>
      <Link
        href={`${Routes.product}/${id}`}
        data-product-impression={getImpressionAttributeData(
          mhubProductColor,
          list
        )}
      >
        <BorderedBox
          data-testid="productDetailColor"
          onMouseLeave={() => handleMouseEvent(null, null)}
          onMouseEnter={() => handleMouseEvent(image?.url, id)}
          currentImage={client && currentImage}
          onClick={() => {
            pushShowProductEvent(mhubProductColor, list)
          }}
        >
          <SlideWrapper>
            <PictureWithPlaceholder
              src={applyImagePolicy(image.url, {
                size: ImageSizes['100x150'],
                mode: ImageMode.Local
              })}
              ratio={1.5}
              transition={0.01}
              disableBackground={!client}
              {...rest}
            />
          </SlideWrapper>
        </BorderedBox>
      </Link>
    </div>
  )
}

export default ProductDetailColor

const BorderedBox = styled(Box)`
  border: 2px solid
    ${(props) =>
      props.currentImage ? props.theme.colors.onBackground : 'transparent'};
`
const OverLayImages = css`
  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.04);
    z-index: 1;
  }
`
const SlideWrapper = styled.div`
  position: relative;
  ${OverLayImages};
`
